import React, { useCallback, useMemo, useRef } from 'react';
import { GeneralModal } from 'ui/GeneralModal';
import FluidButton from 'ui/FluidButton';
import { Field, Formik, FormikProps } from 'formik';
import { ICrmActivityFormValues } from '../types';
import { IMultiselectValueLabelPair, Multiselect } from 'ui/Multiselect';
import { generateArrayOfDatesBetween, getLocalDateAndTime, isBlank } from 'utils';
import { validateCrmCreateActivityForm } from './formValidation';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { format } from 'date-fns';
import { StyledDateRangeInput } from 'containers/BookingList/StyledFilters';
import { TextInput } from 'ui/TextInput';
import Editor from 'pureUi/Editor';
import { useCurrentWidth } from 'effects';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';
import { useCreateActivityMutation } from 'services/CrmApi/mutations/useCreateActivityMutation';
import { ECrmActivityType, ICrmActivity, IUpsertActivityRequest } from 'services/CrmApi/types/CrmActivityTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from 'hooks/useNotifications';
import { getCurrentDateAndTime } from 'containers/CRM/helpers';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { ECrmDirectoryEntryType, IDirectoryEntry } from 'services/CrmApi/types/DirectoryEntry';
import { useUpdateActivityMutation } from 'services/CrmApi/mutations/useUpdateActivityMutation';
import { useBackendUsers } from 'services/BackendApi/queries/useBackendUsers';
import { useCrmCategories } from 'services/CrmApi/queries/useCrmCategories';
import { CategoryDropdown } from 'ui/CategoryDropdown/CategoryDropdown';
import classnames from 'classnames';
import { IUseCrmSearchDirectoryEntriesParams, useCrmSearchDirectoryEntries } from 'services/CrmApi/queries/useCrmSearchDirectoryEntries';
import { useCanEditEntry } from 'containers/CRM/hooks/useCanEdit';

interface ICreateActivityModalProps {
  type: ECrmActivityType;
  activity?: ICrmActivity;
  directoryEntryType: ECrmDirectoryEntryType | undefined;
  directoryEntryId: string | undefined;
  onClose: () => void;
  onSuccess: (activity?: ICrmActivity) => void;
}

const getContactOptions = (
  directoryEntryType: ECrmDirectoryEntryType,
  directoryEntry?: IDirectoryEntry
): IMultiselectValueLabelPair[] => {
  if (directoryEntryType === ECrmDirectoryEntryType.PERSON) {
    const contacts = [{ code: directoryEntry?.id, name: directoryEntry?.fullName }];
    const contactOptions = contacts.map(contact => ({
      value: contact.code ?? null,
      label: contact.name ?? '',
    }));
    return contactOptions;
  } else if (directoryEntryType === ECrmDirectoryEntryType.COMPANY) {
    const contacts = [...(directoryEntry?.linkedDirectoryEntries || [])].filter(
      directoryEntry => directoryEntry.type === ECrmDirectoryEntryType.PERSON
    );
    const contactOptions = contacts.map(contact => ({
      value: contact.id ?? null,
      label: contact.fullName ?? '',
    }));
    return contactOptions;
  } else {
    return [];
  }
};

export const CreateActivityModal: React.FC<ICreateActivityModalProps> = ({
  type,
  activity,
  directoryEntryId,
  directoryEntryType,
  onSuccess,
  onClose,
}) => {
  const { isMobile } = useCurrentWidth();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const queryClient = useQueryClient();
  const { crmDirectoryEntryResponse, directoryEntry } = useCrmDirectoryEntry(directoryEntryId, { enabled: !!directoryEntryId });
  const canSubmit = useCanEditEntry(directoryEntryId, directoryEntryType);

  const params: IUseCrmSearchDirectoryEntriesParams = {
    filter: {
      term: '',
      directoryEntryType: ECrmDirectoryEntryType.PERSON,
    },
    pagination: {
      limit: 2000,
      offset: 0,
    },
  };
  const { crmDirectoryEntries } = useCrmSearchDirectoryEntries(params);
  const allContactOptions: IMultiselectValueLabelPair[] = crmDirectoryEntries.data?.data?.list.map(directoryEntry => ({
    value: directoryEntry.id ?? null,
    label: directoryEntry.name ?? '',
  })) ?? [];
  
  const contactOptions = directoryEntryType ? getContactOptions(directoryEntryType, directoryEntry) : allContactOptions;
  const backendUsers = useBackendUsers('');
  const mentions =
    backendUsers.data?.data?.data?.map(user => ({
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      role: user.type,
      id: user.uuid,
    })) ?? [];

  const { crmCategories } = useCrmCategories();

  const titleMapping = {
    [ECrmActivityType.CALL]: 'Call',
    [ECrmActivityType.MEETING]: 'Meeting',
    [ECrmActivityType.NOTE]: 'Note',
  };
  const newOrEdit = activity ? 'Edit' : 'New';

  const createActivityMutation = useCreateActivityMutation();
  const updateActivityMutation = useUpdateActivityMutation();

  const defaultDateTime = useRef(getCurrentDateAndTime());
  const initialValuesRef = useRef<ICrmActivityFormValues | null>(null);
  const initialValues: ICrmActivityFormValues = useMemo(() => {
    const { date, time } = defaultDateTime.current;
    const activityContactIds = activity?.participantDirectoryEntries
      .map(item => item.id)
      // filter out contactIds that are not in the contactOptions
      // otherwise Multiselect will show nothing selected if it gets at least one id that does not exist in  options
      .filter(contactId => contactOptions.find(option => option.value === contactId));
    const { localDate, localTime } = getLocalDateAndTime(activity?.date);

    const isPerson = directoryEntry?.type === ECrmDirectoryEntryType.PERSON;
    const currentDirectoryEntryIdAsArray = directoryEntry?.id && isPerson ? [directoryEntry?.id] : [];
    const participantDirectoryEntryIds = activityContactIds ?? currentDirectoryEntryIdAsArray;

    const newValues: ICrmActivityFormValues = {
      participantDirectoryEntryIds,
      date: localDate ?? date,
      time: localTime ?? time,
      categories: activity?.categories.map(category => category.id) ?? [],
      topic: activity?.title ?? '',
      description: activity?.description ?? '',
    };
    
    if (initialValuesRef.current && JSON.stringify(initialValuesRef.current) === JSON.stringify(newValues)) {
      return initialValuesRef.current;
    }

    initialValuesRef.current = newValues;
    return newValues;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, directoryEntry, contactOptions]);

  const handleFormSubmit = useCallback(
    async (values: ICrmActivityFormValues) => {
      const date = [new Date(values.date).toISOString().split('T')[0], values.time].join(' ');
      const requestData: IUpsertActivityRequest = {
        type: type,
        date: new Date(date).toISOString(),
        title: values.topic,
        description: values.description ?? null,
        participantDirectoryEntryIds: values.participantDirectoryEntryIds,
        categoryIds: values.categories,
      };

      try {
        if (activity && updateActivityMutation) {
          await updateActivityMutation.mutateAsync({ activityId: activity.id, requestData });
        } else {
          await createActivityMutation.mutateAsync(requestData);
        }

        const successMessage = activity ? 'Activity updated successfully.' : 'Activity created successfully.';
        showSuccessNotification(successMessage);
        onSuccess(activity);
        onClose();
      } catch (error) {
        const errorMessage = activity ? 'Error updating activity.' : 'Error creating activity.';
        showErrorNotification(errorMessage);
      }
    },
    [createActivityMutation, onClose, queryClient, showErrorNotification, showSuccessNotification, type, onSuccess]
  );

  if (crmDirectoryEntries.isFetching || crmDirectoryEntryResponse.isFetching) {
    return (
      <GeneralModal
        onClose={onClose}
        modalWindowClassName="w-[1100px] px-[34px] py-25px rounded h-[90vh] overflow-y-auto"
        shouldCloseByClickingOutside={false}
      >
        <div className="mt-[20px]">
          <LoadingBar />
        </div>
      </GeneralModal>
    );
  }

  return (
    <GeneralModal
      onClose={onClose}
      modalWindowClassName="w-[1100px] px-[34px] py-25px rounded h-[90vh] overflow-y-auto"
      shouldCloseByClickingOutside={false}
    >
      <Formik initialValues={initialValues} validate={validateCrmCreateActivityForm} onSubmit={handleFormSubmit} enableReinitialize>
        {(form: FormikProps<ICrmActivityFormValues>) => {
          return (
            <form onSubmit={form.handleSubmit} className="h-full" autoComplete="off">
              <div className="flex flex-col gap-[25px] h-full">
                <h3 className="font-noe-display font-normal text-21px leading-27px text-black mt-0 mb-10px">
                  {newOrEdit} {titleMapping[type]}
                </h3>

                <div className="contact-and-date flex flex-col md:flex-row gap-[20px] w-full">
                  <div className="contact flex flex-1 flex-col justify-between min-w-0 w-full md:w-auto">
                    <label htmlFor="contact" className="font-pt-sans text-[16px] text-black font-[700] mb-[5px]">
                      Contact *
                    </label>
                    <Field name="participantDirectoryEntryIds">
                      {({ field: { name, value }, form: { setFieldValue } }) => (
                        <div className="flex flex-col flex-1 relative min-w-0 ">
                          <Multiselect
                            className="contact min-w-290px"
                            primaryCtaClassName={classnames('', {
                              'border border-solid border-red-95 bg-red-25': form.touched.participantDirectoryEntryIds && form.errors.participantDirectoryEntryIds,
                            })}
                            itemsClassname="bg-ivory"
                            itemCtaClassName="hover:bg-gray-20"
                            onUpdate={sv => {
                              setFieldValue(name, sv);
                            }}
                            itemsToDisplayBeforeScroll={6}
                            selectedValues={value}
                            options={contactOptions}
                            disabled={directoryEntryType === ECrmDirectoryEntryType.PERSON} // always disabled on contact page
                          />
                          {form.touched.participantDirectoryEntryIds && form.errors.participantDirectoryEntryIds ? (
                            <p className={`text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0`}>
                              {form.errors.participantDirectoryEntryIds}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className="date-time flex gap-[20px]">
                    <div className="date flex-1 ">
                      <Field name="date">
                        {({ field: { name, value }, form: { setFieldValue } }) => {
                          const selectedDates = generateArrayOfDatesBetween(value, value).map(
                            d => `${d}T00:00:00.000Z`
                          );
                          return (
                            <div className="flex flex-col flex-1 relative ">
                              <label className="font-pt-sans text-[16px] text-black font-[700] mb-[5px]">Date *</label>
                              <DatePickerStateProvider
                                defaultSelectedDates={selectedDates}
                                placeholder="SELECT DATE"
                                onDateChange={dates => {
                                  const date = format(new Date(dates[0]), 'yyyy-MM-dd');
                                  setFieldValue(name, date);
                                }}
                                isSingleDateSelection
                                render={(params: IDatePickerSateParams) => {
                                  return (
                                    <StyledDateRangeInput
                                      displayString={params.displayString}
                                      currentDate={params.datePickerCurrentDate}
                                      totalNights={params.totalNights}
                                      selectedDates={params.selectedDates}
                                      onDayClick={params.handleDayClick}
                                      onDayMouseOver={params.handleDateMouseOver}
                                      showDatePicker={params.showDatePicker}
                                      onNextClick={params.incrementDate}
                                      onPrevClick={params.decrementDate}
                                      onMouseDown={params.toggleDatePicker}
                                      onClickOutside={params.hideDatePicker}
                                      calendarCount={1}
                                      customWidth={1}
                                      noPortal
                                      showTotalNights={false}
                                      enablePastDates
                                    />
                                  );
                                }}
                              />
                            </div>
                          );
                        }}
                      </Field>
                    </div>
                    <div className="time flex-1">
                      <label htmlFor="time" className="font-pt-sans text-[16px] text-black font-[700] mb-[5px]">
                        Time *
                      </label>
                      <Field
                        as={TextInput}
                        type="time"
                        name="time"
                        id="time"
                        className="time flex-1 "
                        errorMessage={form.touched.time && form.errors.time ? form.errors.time : null}
                        errorClassName="time-error"
                      />
                    </div>
                  </div>
                </div>

                <div className="categories flex flex-col w-full">
                  <label htmlFor="categories" className="font-pt-sans text-[16px] text-black font-[700] mb-[5px]">
                    Categories *
                  </label>
                  <Field name="categories">
                    {({ field: { name, value }, form: { setFieldValue } }) => {
                      return (
                        <div className="flex flex-col flex-1 relative ">
                          <CategoryDropdown
                            optionGroups={crmCategories.data?.data ?? []}
                            selectedOptions={value ?? []}
                            onSelectedOptionsChange={(categoryIds: string[]) => {
                              setFieldValue(name, categoryIds);
                            }}
                            noOptionsMessage="No categories found"
                            hasError={!!(form.touched.categories && form.errors.categories)}
                          />
                          {form.touched.categories && form.errors.categories ? (
                            <p className={`text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0`}>
                              {form.errors.categories}
                            </p>
                          ) : null}
                        </div>
                      );
                    }}
                  </Field>
                </div>

                <div className="topic flex flex-col w-full">
                  <label htmlFor="topic" className="font-pt-sans text-[16px] text-black font-[700] mb-[5px]">
                    Topic *
                  </label>
                  <Field
                    as={TextInput}
                    type="text"
                    name="topic"
                    id="topic"
                    className="topic flex-1"
                    errorMessage={form.touched.topic && form.errors.topic ? form.errors.topic : null}
                    errorClassName="topic-error"
                  />
                </div>

                <div className="description flex flex-1 md:flex-0 gap-[20px]">
                  <Field name="description">
                    {({ field: { name, value }, form: { setFieldValue } }) => (
                      <div className="flex flex-col relative w-full">
                        <Editor
                          text={value}
                          handleEditorChange={value => setFieldValue(name, value)}
                          options={mentions}
                          mentionAbility={true}
                          placeHolder="Type @ to mention and notify someone."
                          flex={isMobile ? '1' : undefined}
                        />
                      </div>
                    )}
                  </Field>
                </div>

                <div className="buttons flex pb-[25px]">
                  <FluidButton type="secondary" onClick={onClose}>
                    Cancel
                  </FluidButton>
                  <FluidButton
                    type="primary"
                    isLoading={createActivityMutation.isPending || updateActivityMutation.isPending}
                    className="ml-10px"
                    submit
                    disabled={!canSubmit}
                  >
                    Save
                  </FluidButton>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </GeneralModal>
  );
};
